import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { OtherFilters, type OtherFiltersProps } from '~/components/Filters/OtherFilters/OtherFilters';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import {
  addOtherFilterValueAction,
  clearOtherFiltersValuesAction,
  removeOtherFilterValueAction,
} from '~/store/filters/actions';
import { hasOtherFiltersValuesSelector, selectedOtherFiltersSelector } from '~/store/filters/selectors';
import { type OtherFiltersType } from '~/store/filters/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type SingleOtherFiltersProps = {
  otherFilters?: OtherFiltersProps['otherFilters'];
} & SingleFilterProps;

export const SingleOtherFilters = memo(({ otherFilters, onApplyClick }: SingleOtherFiltersProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector(hasOtherFiltersValuesSelector);
  const selectedFilters = useSelector(selectedOtherFiltersSelector);
  const clearValues = useCallback(() => {
    dispatch(clearOtherFiltersValuesAction());
  }, [dispatch]);

  const onTypeSelectedChange = useCallback(
    (filter: OtherFiltersType, isSelected: boolean) => {
      if (isSelected) {
        dispatch(addOtherFilterValueAction({ filter }));
      } else {
        dispatch(removeOtherFilterValueAction({ filter }));
      }

      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="other"
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <OtherFilters
        otherFilters={otherFilters}
        onTypeSelectedChange={onTypeSelectedChange}
        selectedFilters={selectedFilters}
      />
    </SingleFilterContainer>
  );
});

SingleOtherFilters.displayName = 'SingleOtherFilters';
